import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface SearchableSingleSelectItem {
  id: string;
  label: string;
  data?: unknown;
}

@Component({
  selector: 'app-searchable-single-select',
  templateUrl: './searchable-single-select.component.html',
  styleUrls: ['./searchable-single-select.component.scss'],
  standalone: false,
})
export class SearchableSingleSelectComponent implements OnDestroy {
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() emptyText = '';
  @Input() items: SearchableSingleSelectItem[] = [];
  @Input() value: SearchableSingleSelectItem | null = null;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() debounceMs = 300;
  @Input() hasError = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() valueChange = new EventEmitter<SearchableSingleSelectItem>();

  @ViewChild('rootEl', { static: true }) rootEl!: ElementRef<HTMLElement>;

  isOpen = false;
  searchText = '';

  private readonly searchSubject = new Subject<string>();
  private readonly searchSub: Subscription;

  constructor() {
    this.searchSub = this.searchSubject
      .pipe(debounceTime(this.debounceMs), distinctUntilChanged())
      .subscribe((q) => this.searchChange.emit(q));
  }

  ngOnDestroy(): void {
    this.searchSub.unsubscribe();
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchText = '';
      this.searchChange.emit('');
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  selectItem(item: SearchableSingleSelectItem): void {
    this.valueChange.emit(item);
    this.isOpen = false;
  }

  isSelected(item: SearchableSingleSelectItem): boolean {
    return !!this.value && this.value.id === item.id;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.rootEl.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }
}
